import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { Users, Mic, Hourglass, PartyPopper, ShieldX } from 'lucide-react';
import { openSessionSocket } from './liveApi';

// Lobby côté participant : présences en direct, attente du démarrage.
const Lobby = ({ joinInfo }) => {
  const { participation_id: participationId, session, ws_token: wsToken } = joinInfo;
  const [participants, setParticipants] = useState([]);
  const [phase, setPhase] = useState('lobby'); // lobby | started | kicked | closed
  const socketRef = useRef(null);

  useEffect(() => {
    const socket = openSessionSocket(session.id, wsToken, {
      onMessage: (message) => {
        switch (message.type) {
          case 'lobby_state':
            setParticipants(message.participants);
            if (message.session_status === 'in_progress') setPhase('started');
            break;
          case 'participant_joined':
            setParticipants((current) => {
              const others = current.filter(
                (p) => p.participation_id !== message.participation_id
              );
              return [...others, { ...message, connected: true }];
            });
            break;
          case 'participant_left':
            setParticipants((current) =>
              current.filter((p) => p.participation_id !== message.participation_id)
            );
            break;
          case 'kicked':
            setPhase('kicked');
            break;
          case 'session_started':
            setPhase('started');
            break;
          default:
            break;
        }
      },
      onClose: () => {
        setPhase((current) => (current === 'lobby' ? 'closed' : current));
      },
    });
    socketRef.current = socket;
    return () => socket.close();
  }, [session.id, wsToken]);

  if (phase === 'kicked') {
    return (
      <CenterScreen icon={<ShieldX className="w-16 h-16 text-red-500" />}
        title="Vous avez été retiré de la session"
        subtitle="Contactez l'animateur si c'est une erreur." />
    );
  }

  if (phase === 'closed') {
    return (
      <CenterScreen icon={<Hourglass className="w-16 h-16 text-gray-400" />}
        title="Session terminée"
        subtitle="Cette session n'est plus disponible." />
    );
  }

  if (phase === 'started') {
    return (
      <CenterScreen icon={<PartyPopper className="w-16 h-16 text-indigo-500" />}
        title="C'est parti !"
        subtitle="La première manche arrive… (déroulé du jeu : prochain lot)" />
    );
  }

  const me = participants.find((p) => p.participation_id === participationId);

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-600 to-purple-700 p-6 flex items-center justify-center">
      <Card className="w-full max-w-2xl">
        <CardHeader className="text-center">
          <CardTitle className="text-2xl">En attente de l'animateur…</CardTitle>
          <CardDescription>
            Vous êtes connecté en tant que <strong>{me ? me.nickname : '…'}</strong>
            {me && (
              <Badge variant="secondary" className="ml-2">
                {me.role === 'storyteller' ? 'Conteur' : 'Public'}
              </Badge>
            )}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center text-sm text-gray-500 mb-4">
            <Users className="w-4 h-4 mr-2" />
            {participants.length} participant{participants.length > 1 ? 's' : ''} dans le lobby
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {participants.map((participant) => (
              <div key={participant.participation_id}
                className="flex items-center space-x-2 rounded-lg border p-2">
                <Avatar className="h-8 w-8">
                  <AvatarFallback>
                    {participant.nickname.slice(0, 2).toUpperCase()}
                  </AvatarFallback>
                </Avatar>
                <span className="truncate text-sm">{participant.nickname}</span>
                {participant.role === 'storyteller' && (
                  <Mic className="w-3 h-3 text-indigo-500 shrink-0" />
                )}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

const CenterScreen = ({ icon, title, subtitle }) => (
  <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6">
    <div className="text-center space-y-4">
      <div className="flex justify-center">{icon}</div>
      <h1 className="text-2xl font-bold">{title}</h1>
      <p className="text-gray-500">{subtitle}</p>
    </div>
  </div>
);

export default Lobby;
